import { DndContext, PointerSensor, KeyboardSensor, useSensor, useSensors, closestCenter } from "@dnd-kit/core";
import { SortableContext, useSortable, arrayMove, verticalListSortingStrategy, sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MathfieldElement } from "mathlive";
import { BlockMath, InlineMath } from "react-katex";
import "katex/dist/katex.min.css";

const QUESTIONS = [
  {
    "id": "q1",
    "instruction": "Answer each part.",
    "parts": [
      {
        "instruction": "Write as an ordinary number.",
        "latex": "1.2 \\times 10^{5}",
        "answer": "120000",
        "standard": false
      },
      {
        "instruction": "Write in standard form.",
        "latex": "0.003",
        "answer": "3 \\times 10^{-3}",
        "standard": true
      }
    ],
    "latex": "(a) Write as an ordinary number. 1.2 \\times 10^{5} ; (b) Write in standard form. 0.003",
    "displayAnswer": "\\begin{aligned}\\text{(a)}\\quad 120000 \\\\ \\text{(b)}\\quad 3 \\times 10^{-3}\\end{aligned}"
  },
  {
    "id": "q2",
    "instruction": "Answer each part.",
    "parts": [
      {
        "instruction": "Write in standard form.",
        "latex": "42\\,900\\,000",
        "answer": "4.29 \\times 10^{7}",
        "standard": true
      },
      {
        "instruction": "Write as an ordinary number.",
        "latex": "3.61 \\times 10^{-3}",
        "answer": "0.00361",
        "standard": false
      }
    ],
    "latex": "(a) Write in standard form. 42\\,900\\,000 ; (b) Write as an ordinary number. 3.61 \\times 10^{-3}",
    "displayAnswer": "\\begin{aligned}\\text{(a)}\\quad 4.29 \\times 10^{7} \\\\ \\text{(b)}\\quad 0.00361\\end{aligned}"
  },
  {
    "id": "q3",
    "instruction": "Answer each part.",
    "parts": [
      {
        "instruction": "Write as an ordinary number.",
        "latex": "9.516 \\times 10^{6}",
        "calculator": "no",
        "answer": "9516000",
        "standard": false
      },
      {
        "instruction": "Write in standard form.",
        "latex": "0.0724",
        "calculator": "no",
        "answer": "7.24 \\times 10^{-2}",
        "standard": true
      },
      {
        "instruction": "Calculate. Give your answer in standard form.",
        "latex": "(8.694 \\times 10^{2}) \\div (6.21 \\times 10^{-3})",
        "calculator": "yes",
        "answer": "1.4 \\times 10^{5}",
        "standard": true
      }
    ],
    "latex": "(a) Write as an ordinary number. 9.516 \\times 10^{6} ; (b) Write in standard form. 0.0724 ; (c) Calculate. Give your answer in standard form. (8.694 \\times 10^{2}) \\div (6.21 \\times 10^{-3})",
    "displayAnswer": "\\begin{aligned}\\text{(a)}\\quad 9516000 \\\\ \\text{(b)}\\quad 7.24 \\times 10^{-2} \\\\ \\text{(c)}\\quad 1.4 \\times 10^{5}\\end{aligned}"
  },
  {
    "id": "q4",
    "instruction": "Answer each part.",
    "parts": [
      {
        "instruction": "Write as an ordinary number.",
        "latex": "5.12 \\times 10^{-5}",
        "answer": "0.0000512",
        "standard": false
      },
      {
        "instruction": "Write in standard form.",
        "latex": "5\\,600\\,000",
        "answer": "5.6 \\times 10^{6}",
        "standard": true
      }
    ],
    "latex": "(a) Write as an ordinary number. 5.12 \\times 10^{-5} ; (b) Write in standard form. 5\\,600\\,000",
    "displayAnswer": "\\begin{aligned}\\text{(a)}\\quad 0.0000512 \\\\ \\text{(b)}\\quad 5.6 \\times 10^{6}\\end{aligned}"
  },
  {
    "id": "q5",
    "instruction": "Answer each part.",
    "parts": [
      {
        "instruction": "Write in standard form.",
        "latex": "0.0065",
        "answer": "6.5 \\times 10^{-3}",
        "standard": true
      },
      {
        "instruction": "Write as an ordinary number.",
        "latex": "3 \\times 10^{4}",
        "answer": "30000",
        "standard": false
      }
    ],
    "latex": "(a) Write in standard form. 0.0065 ; (b) Write as an ordinary number. 3 \\times 10^{4}",
    "displayAnswer": "\\begin{aligned}\\text{(a)}\\quad 6.5 \\times 10^{-3} \\\\ \\text{(b)}\\quad 30000\\end{aligned}"
  },
  {
    "id": "q6",
    "instruction": "Answer each part.",
    "parts": [
      {
        "instruction": "Write as an ordinary number.",
        "latex": "3.08 \\times 10^{-5}",
        "answer": "0.0000308",
        "standard": false
      },
      {
        "instruction": "Write in standard form.",
        "latex": "\\text{5 million}",
        "answer": "5 \\times 10^{6}",
        "standard": true
      },
      {
        "instruction": "Calculate. Give your answer in standard form.",
        "latex": "(6.3 \\times 10^{5}) \\times (2.5 \\times 10^{-2})",
        "calculator": "yes",
        "answer": "1.575 \\times 10^{4}",
        "standard": true
      }
    ],
    "latex": "(a) Write as an ordinary number. 3.08 \\times 10^{-5} ; (b) Write in standard form. \\text{5 million} ; (c) Calculate. Give your answer in standard form. (6.3 \\times 10^{5}) \\times (2.5 \\times 10^{-2})",
    "displayAnswer": "\\begin{aligned}\\text{(a)}\\quad 0.0000308 \\\\ \\text{(b)}\\quad 5 \\times 10^{6} \\\\ \\text{(c)}\\quad 1.575 \\times 10^{4}\\end{aligned}"
  },
  {
    "id": "q7",
    "instruction": "Calculate. Give your answer in standard form.",
    "parts": [
      {
        "instruction": "Calculate. Give your answer in standard form.",
        "latex": "(8.69 \\times 10^{-5}) \\div (5.5 \\times 10^{-7})",
        "calculator": "yes",
        "answer": "1.58 \\times 10^{2}",
        "standard": true
      }
    ],
    "latex": "Calculate. Give your answer in standard form. (8.69 \\times 10^{-5}) \\div (5.5 \\times 10^{-7})",
    "displayAnswer": "1.58 \\times 10^{2}"
  },
  {
    "id": "q9",
    "instruction": "Answer each part.",
    "parts": [
      {
        "instruction": "Write as an ordinary number.",
        "latex": "5.2 \\times 10^{-1}",
        "answer": "0.52",
        "standard": false
      },
      {
        "instruction": "Calculate. Give your answer in standard form.",
        "latex": "(3.2 \\times 10^{3}) \\times (6.5 \\times 10^{4})",
        "answer": "2.08 \\times 10^{8}",
        "standard": true
      }
    ],
    "latex": "(a) Write as an ordinary number. 5.2 \\times 10^{-1} ; (b) Calculate. Give your answer in standard form. (3.2 \\times 10^{3}) \\times (6.5 \\times 10^{4})",
    "displayAnswer": "\\begin{aligned}\\text{(a)}\\quad 0.52 \\\\ \\text{(b)}\\quad 2.08 \\times 10^{8}\\end{aligned}"
  },
  {
    "id": "q10",
    "instruction": "Write in standard form.",
    "parts": [
      {
        "instruction": "Write in standard form.",
        "latex": "0.21 \\times 10^{6}",
        "answer": "2.1 \\times 10^{5}",
        "standard": true
      }
    ],
    "latex": "Write in standard form. 0.21 \\times 10^{6}",
    "displayAnswer": "2.1 \\times 10^{5}"
  },
  {
    "id": "q11",
    "instruction": "Calculate. Give your answer as an ordinary number.",
    "parts": [
      {
        "instruction": "Calculate. Give your answer as an ordinary number.",
        "latex": "(6.7 \\times 10^{4}) \\times (3.4 \\times 10^{-8})",
        "answer": "0.002278",
        "standard": false
      }
    ],
    "latex": "Calculate. Give your answer as an ordinary number. (6.7 \\times 10^{4}) \\times (3.4 \\times 10^{-8})",
    "displayAnswer": "0.002278"
  },
  {
    "id": "q12",
    "instruction": "Calculate. Give your answer in standard form.",
    "parts": [
      {
        "instruction": "Calculate. Give your answer in standard form.",
        "latex": "\\frac{0.03 \\times 0.02}{0.008}",
        "answer": "7.5 \\times 10^{-2}",
        "standard": true
      }
    ],
    "latex": "Calculate. Give your answer in standard form. \\frac{0.03 \\times 0.02}{0.008}",
    "displayAnswer": "7.5 \\times 10^{-2}"
  },
  {
    "id": "q13",
    "instruction": "Calculate. Give your answer in standard form.",
    "parts": [
      {
        "instruction": "Calculate. Give your answer in standard form.",
        "latex": "\\frac{3.744 \\times 10^{9}}{2.4 \\times 10^{5}}",
        "answer": "1.56 \\times 10^{4}",
        "standard": true,
        "calculator": "yes"
      }
    ],
    "latex": "Calculate. Give your answer in standard form. \\frac{3.744 \\times 10^{9}}{2.4 \\times 10^{5}}",
    "displayAnswer": "1.56 \\times 10^{4}",
    "calculator": "yes"
  },
  {
    "id": "q14",
    "instruction": "Calculate. Give your answer in standard form.",
    "parts": [
      {
        "instruction": "Calculate. Give your answer in standard form.",
        "latex": "(5 \\times 10^{3}) \\times (6 \\times 10^{7})",
        "answer": "3 \\times 10^{11}",
        "standard": true
      }
    ],
    "latex": "Calculate. Give your answer in standard form. (5 \\times 10^{3}) \\times (6 \\times 10^{7})",
    "displayAnswer": "3 \\times 10^{11}"
  },
  {
    "id": "q15",
    "instruction": "Answer each part.",
    "parts": [
      {
        "instruction": "Write in standard form.",
        "latex": "0.000\\,054\\,376",
        "answer": "5.4376 \\times 10^{-5}",
        "standard": true
      },
      {
        "instruction": "Write as an ordinary number.",
        "latex": "4.15 \\times 10^{6}",
        "answer": "4150000",
        "standard": false
      },
      {
        "instruction": "Calculate. Give your answer in standard form.",
        "latex": "\\frac{4.1 \\times 10^{5} \\times 7.3 \\times 10^{4}}{2 \\times 10^{-6}}",
        "answer": "1.4965 \\times 10^{16}",
        "standard": true,
        "calculator": "yes"
      }
    ],
    "latex": "(a) Write in standard form. 0.000\\,054\\,376 ; (b) Write as an ordinary number. 4.15 \\times 10^{6} ; (c) Calculate. Give your answer in standard form. \\frac{4.1 \\times 10^{5} \\times 7.3 \\times 10^{4}}{2 \\times 10^{-6}}",
    "displayAnswer": "\\begin{aligned}\\text{(a)}\\quad 5.4376 \\times 10^{-5} \\\\ \\text{(b)}\\quad 4150000 \\\\ \\text{(c)}\\quad 1.4965 \\times 10^{16}\\end{aligned}"
  },
  {
    "id": "q16",
    "type": "order",
    "instruction": "Order these numbers from smallest to largest.",
    "latex": "6.1 \\times 10^{2},\\quad 0.061 \\times 10^{2},\\quad 6100 \\times 10^{-4},\\quad 61",
    "options": [
      {
        "id": "a",
        "latex": "6.1 \\times 10^{2}"
      },
      {
        "id": "b",
        "latex": "0.061 \\times 10^{2}"
      },
      {
        "id": "c",
        "latex": "6100 \\times 10^{-4}"
      },
      {
        "id": "d",
        "latex": "61"
      }
    ],
    "answers": [
      "c|b|d|a"
    ],
    "displayAnswer": "6100 \\times 10^{-4} < 0.061 \\times 10^{2} < 61 < 6.1 \\times 10^{2}"
  }
];

const STORAGE_KEY = "standard_form_quiz_v3";
const SUBMIT_URL =
  `${process.env.REACT_APP_HOMEWORK_API_URL || (process.env.NODE_ENV === "development" ? "http://localhost:5001" : "")}/api/homework/standard-form`;

function parseParts(raw) {
  try { const result = JSON.parse(raw || "{}"); return result && typeof result === "object" ? result : {}; } catch { return {}; }
}

function standardPair(raw) {
  const value = String(raw).replace(/\\left|\\right|\\,/g, "").replace(/\\times|\\cdot|\u00d7/g, "*").replace(/[{}\s]/g, "");
  const match = value.match(/^([+]?(?:\d+(?:\.\d*)?|\.\d+))\*?10\^(-?\d+)$/);
  return match ? [Number(match[1]), Number(match[2])] : null;
}

function matchesPart(raw, part) {
  if (part.standard) {
    const actual = standardPair(raw), expected = standardPair(part.answer);
    return Boolean(actual && expected && actual[0] >= 1 && actual[0] < 10 && actual[0] === expected[0] && actual[1] === expected[1]);
  }
  const value = String(raw).replace(/\\,/g, "").replace(/[\s,]/g, "");
  return /^(?:\d+(?:\.\d*)?|\.\d+)$/.test(value) && Number(value) === Number(part.answer);
}

function StandardFormAnswer({ value, onChange, readOnly, fieldStyle, partLabel }) {
  const split = String(value || "").match(/^(.*?)\\times10\^\{(.*?)\}$/);
  const pair = standardPair(value);
  const number = split ? split[1] : pair ? String(pair[0]) : "";
  const power = split ? split[2] : pair ? String(pair[1]) : "";
  const update = (coefficient, exponent) => onChange(`${coefficient}\\times10^{${exponent}}`);
  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 0", flexWrap: "wrap" }}>
        <input type="text" inputMode="decimal" aria-label={`Number for answer ${partLabel}`}
          readOnly={readOnly} value={number} onChange={(event) => update(event.target.value, power)}
          style={{ ...styles.mathInput, width: `calc(${Math.max(3, number.length + 1)}ch + 1.3rem + 4px)`, minWidth: 72, maxWidth: "100%", textAlign: "center", marginTop: 0, ...fieldStyle }} />
        <span aria-hidden="true" style={{ fontSize: "1.5rem", color: "#17324d" }}>× 10</span>
        <input type="text" inputMode="text" aria-label={`Power of ten for answer ${partLabel}`}
          readOnly={readOnly} value={power} onChange={(event) => update(number, event.target.value)}
          style={{ ...styles.mathInput, width: 48, padding: "0.45rem 0.25rem", textAlign: "center", marginTop: 0, alignSelf: "flex-start", transform: "translateY(-20px)", fontSize: "1rem", ...fieldStyle }} />
      </div>
    </div>
  );
}

function workingParts(raw) {
  try { const values = JSON.parse(raw || "{}"); return values && typeof values === "object" ? values : { 0: raw || "" }; }
  catch { return { 0: raw || "" }; }
}

function formatWorking(question, raw) {
  if (question.type === "order") return raw || "";
  const values = workingParts(raw);
  return question.parts.map((_, index) => {
    const value = values[index] || "";
    return value ? (question.parts.length > 1 ? "(" + String.fromCharCode(97 + index) + ") " : "") + value : "";
  }).filter(Boolean).join("\n\n");
}

function formatAttempt(question, raw) {
  if (!raw) return "No answer";
  if (question.type === "order") return raw.split("|").map((id) => question.options.find((option) => option.id === id)?.latex || id).join(" < ");
  const values = parseParts(raw);
  return question.parts.map((part, index) => (question.parts.length > 1 ? "(" + String.fromCharCode(97 + index) + ") " : "") + (values[index] || "No answer")).join("; ");
}

function screenSnapshot(question, raw, working) {
  if (question.type === "order") {
    const ids = raw ? raw.split("|") : question.options.map((option) => option.id);
    return { type: "order", instruction: question.instruction, working: working || "", status: question.answers.includes(raw) ? "correct" : "incorrect",
      options: ids.map((id) => question.options.find((option) => option.id === id)), calculator: question.calculator || "no" };
  }
  const values = parseParts(raw), workings = workingParts(working);
  return { instruction: question.instruction, parts: question.parts.map((part, index) => ({ ...part,
    studentAnswer: values[index] || "", working: workings[index] || "", showWorking: part.instruction.startsWith("Calculate."),
    status: matchesPart(values[index] || "", part) ? "correct" : "incorrect", calculator: part.calculator || question.calculator || "no" })) };
}

function SortableNumber({ option, index, locked, fieldStyle }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: option.id, disabled: locked });
  const colors = ["#e0f2fe", "#ede9fe", "#fef3c7", "#dcfce7"];
  return (
    <div ref={setNodeRef} style={{ transform: CSS.Transform.toString(transform), transition, position: "relative", zIndex: isDragging ? 2 : 0 }}>
      <button type="button" {...attributes} {...listeners} disabled={locked}
        aria-label={`Position ${index + 1}: ${option.latex}. Press Space to pick up, arrow keys to move, then Space to drop.`}
        style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, width: "100%", padding: "16px 20px",
          border: "2px solid #cbd5e1", borderRadius: 18, backgroundColor: colors[index % colors.length], color: "#17324d",
          fontSize: "1.15rem", cursor: locked ? "default" : "grab", touchAction: "none", opacity: 1,
          boxShadow: isDragging ? "0 8px 20px rgba(23,50,77,0.2)" : "0 2px 4px rgba(23,50,77,0.08)", ...fieldStyle }}>
        <span style={{ fontSize: 14, fontWeight: 600 }}>{index + 1}</span>
        <InlineMath math={option.latex} />
        <span aria-hidden="true" style={{ color: "#64748b" }}>⠿</span>
      </button>
    </div>
  );
}

function OrderingAnswer({ question, value, locked, onChange, fieldStyle }) {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }));
  const ids = value ? value.split("|") : question.options.map((option) => option.id);
  return (
    <div>
      <p style={styles.label}>Smallest at the top, largest at the bottom</p>
      {!locked && <p style={{ fontSize: 14, color: "#475569" }}>Drag the cards to reorder them.</p>}
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={({ active, over }) => {
        if (!locked && over && active.id !== over.id) onChange(arrayMove(ids, ids.indexOf(active.id), ids.indexOf(over.id)).join("|"));
      }}>
        <SortableContext items={ids} strategy={verticalListSortingStrategy}>
          <div style={{ display: "grid", gap: 12, marginTop: 16 }}>
            {ids.map((id, index) => <SortableNumber key={id} option={question.options.find((option) => option.id === id)}
              index={index} locked={locked} fieldStyle={fieldStyle} />)}
          </div>
        </SortableContext>
      </DndContext>
    </div>
  );
}

function makeSubmissionId() {
  if (
    window.crypto &&
    typeof window.crypto.randomUUID === "function"
  ) {
    return window.crypto.randomUUID();
  }

  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function ResultIcon({ type, size = 28 }) {
  const isCorrect = type === "correct";
  const colour = isCorrect ? "#138a5b" : "#d64545";
  const paleColour = isCorrect ? "#e7f7ef" : "#fdecec";

  return (
    <svg
      aria-label={isCorrect ? "Correct" : "Incorrect"}
      role="img"
      width={size}
      height={size}
      viewBox="0 0 32 32"
      style={{ display: "inline-block", flexShrink: 0 }}
    >
      <circle cx="16" cy="16" r="14" fill={paleColour} stroke={colour} strokeWidth="2" />
      {isCorrect ? (
        <path
          d="M9.2 16.2l4.3 4.4 9.5-10"
          fill="none"
          stroke={colour}
          strokeWidth="3.2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      ) : (
        <path
          d="M11 11l10 10M21 11L11 21"
          fill="none"
          stroke={colour}
          strokeWidth="3.2"
          strokeLinecap="round"
        />
      )}
    </svg>
  );
}

export default function StandardFormQuiz() {
  const navigate = useNavigate();
  const questions = useMemo(() => QUESTIONS, []);

  const [studentName, setStudentName] = useState("");
  const [nameConfirmed, setNameConfirmed] = useState(false);
  const [current, setCurrent] = useState(0);
  const [studentAnswer, setStudentAnswer] = useState("");
  const [working, setWorking] = useState("");
  const [feedback, setFeedback] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const [questionResults, setQuestionResults] = useState(Array(questions.length).fill(null));
  const [studentAnswers, setStudentAnswers] = useState(Array(questions.length).fill(""));
  const [workingAnswers, setWorkingAnswers] = useState(Array(questions.length).fill(""));
  const [attemptScreens, setAttemptScreens] = useState({});
  const [attemptHistory, setAttemptHistory] = useState(Array.from({ length: questions.length }, () => []));
  const [reviewMode, setReviewMode] = useState(false);
  const [done, setDone] = useState(false);
  const [submissionStatus, setSubmissionStatus] = useState("idle");
  const [submissionId, setSubmissionId] = useState(makeSubmissionId);

  useEffect(() => {
    if (!customElements.get("math-field")) customElements.define("math-field", MathfieldElement);
  }, []);

  useEffect(() => {
    try {
      const data = JSON.parse(localStorage.getItem(STORAGE_KEY) || "null");
      if (!data) return;
      setStudentName(data.studentName || "");
      setNameConfirmed(Boolean(data.nameConfirmed));
      setCurrent(Number.isInteger(data.current) ? data.current : 0);
      if (Array.isArray(data.questionResults) && data.questionResults.length === questions.length) setQuestionResults(data.questionResults);
      if (Array.isArray(data.studentAnswers) && data.studentAnswers.length === questions.length) setStudentAnswers(data.studentAnswers);
      if (Array.isArray(data.workingAnswers) && data.workingAnswers.length === questions.length) setWorkingAnswers(data.workingAnswers);
      if (Array.isArray(data.attemptHistory) && data.attemptHistory.length === questions.length) setAttemptHistory(data.attemptHistory);
      setAttemptScreens(data.attemptScreens || {});
      setDone(Boolean(data.done));
      setSubmissionStatus(data.submissionStatus || "idle");
      setSubmissionId(data.submissionId || makeSubmissionId());
      const index = Number.isInteger(data.current) ? data.current : 0;
      setStudentAnswer(data.studentAnswers?.[index] || "");
      setWorking(data.workingAnswers?.[index] || "");
    } catch {
      localStorage.removeItem(STORAGE_KEY);
    }
  }, [questions.length]);

  const saveProgress = (override = {}) => {
    const payload = {
      studentName, nameConfirmed, current, questionResults, studentAnswers,
      workingAnswers, attemptHistory, attemptScreens, done, submissionStatus, submissionId, ...override,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  };

  const confirmName = (event) => {
    event.preventDefault();
    const cleanName = studentName.trim();
    if (!cleanName) return;
    setStudentName(cleanName);
    setNameConfirmed(true);
    saveProgress({ studentName: cleanName, nameConfirmed: true });
  };

  const checkAnswer = () => {
    const q = questions[current];
    const answer = q.type === "order" ? studentAnswer || q.options.map((option) => option.id).join("|") : studentAnswer;
    const partValues = parseParts(answer);
    if (q.type !== "order" && q.parts.length === 1 && q.parts.some((part, index) => {
      const value = String(partValues[index] || "").trim();
      const fields = value.match(/^(.*?)\\times10\^\{(.*?)\}$/);
      return !value || (part.standard && fields && (!fields[1].trim() || !fields[2].trim()));
    })) {
      setFeedback("Please enter an answer first.");
      return;
    }

    const isCorrect = q.type === "order" ? q.answers.includes(answer)
      : q.parts.every((part, index) => matchesPart(partValues[index], part));
    void playQuizFeedback(isCorrect);
    const updatedStudentAnswers = [...studentAnswers];
    const updatedWorkingAnswers = [...workingAnswers];
    const updatedHistory = attemptHistory.map((history) => [...history]);
    updatedStudentAnswers[current] = answer;
    updatedWorkingAnswers[current] = working;
    updatedHistory[current].push(answer);
    const updatedScreens = { ...attemptScreens, [q.id]: [...(attemptScreens[q.id] || []), screenSnapshot(q, answer, working)] };
    setAttemptScreens(updatedScreens);

    const updatedResults = [...questionResults];
    const newAttempts = attempts + 1;
    setAttempts(newAttempts);
    setStudentAnswers(updatedStudentAnswers);
    setWorkingAnswers(updatedWorkingAnswers);
    setAttemptHistory(updatedHistory);

    if (isCorrect) {
      updatedResults[current] = "correct";
      setQuestionResults(updatedResults);
      setFeedback(
        <div style={styles.statusLine}>
          <ResultIcon type="correct" />
          <strong style={{ color: "#087f5b" }}>Correct!</strong>
        </div>
      );
    } else if (newAttempts >= 3) {
      updatedResults[current] = "incorrect";
      setQuestionResults(updatedResults);
      setFeedback(
        <div>
          <div style={styles.statusLine}>
            <ResultIcon type="incorrect" />
            <strong style={{ color: "#c0392b" }}>Not quite. The correct answer is:</strong>
          </div>
          <BlockMath math={q.displayAnswer} />
          <div style={{ marginTop: 6, opacity: 0.8, fontSize: 14 }}>You can move on now.</div>
        </div>
      );
    } else if (q.parts?.length > 1) {
      setFeedback(null);
    } else {
      setFeedback(
        <div style={styles.statusLine}>
          <ResultIcon type="incorrect" />
          <strong style={{ color: "#c0392b" }}>
            Try again. Attempts left: {3 - newAttempts}
          </strong>
        </div>
      );
    }

    saveProgress({
      studentAnswers: updatedStudentAnswers,
      workingAnswers: updatedWorkingAnswers,
      attemptHistory: updatedHistory,
      attemptScreens: updatedScreens,
      questionResults: updatedResults,
    });
  };

  const canAdvance = questionResults[current] === "correct" || attempts >= 3;

  const submitResults = async (results, answers, histories, workings) => {
    setSubmissionStatus("sending");
    saveProgress({ done: true, questionResults: results, studentAnswers: answers, attemptHistory: histories, workingAnswers: workings, submissionStatus: "sending" });

    const payload = {
      submissionId,
      studentName: studentName.trim(),
      quizTitle: "Standard Form",
      completedAt: new Date().toISOString(),
      score: results.filter((result) => result === "correct").length,
      total: questions.length,
      questions: questions.map((question, index) => ({
        number: index + 1,
        question: `${question.instruction} ${question.latex}`,
        correctAnswer: question.displayAnswer,
        studentAnswer: formatAttempt(question, answers[index]),
        attempts: (histories[index] || []).map((answer) => formatAttempt(question, answer)),
        working: formatWorking(question, workings[index]),
        screen: screenSnapshot(question, answers[index], workings[index]),
        screenAttempts: (histories[index] || []).map((answer, attemptIndex) => {
          const recorded = attemptScreens[question.id] || [];
          const earlierAttempts = (histories[index] || []).length - recorded.length;
          return recorded[attemptIndex - earlierAttempts] || { ...screenSnapshot(question, answer, ""), workingUnavailable: true };
        }),
        result: results[index] || "incorrect",
      })),
    };

    try {
      const response = await fetch(SUBMIT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!response.ok) throw new Error("Submission failed");
      setSubmissionStatus("sent");
      saveProgress({ done: true, submissionStatus: "sent" });
    } catch {
      setSubmissionStatus("failed");
      saveProgress({ done: true, submissionStatus: "failed" });
    }
  };

  const nextQuestion = () => {
    setFeedback(null);
    setAttempts(0);
    const next = current + 1;

    if (next < questions.length) {
      setCurrent(next);
      setStudentAnswer(studentAnswers[next] || "");
      setWorking(workingAnswers[next] || "");
      saveProgress({ current: next });
      return;
    }

    if (reviewMode) {
      navigate("/student-dashboard");
      return;
    }

    setDone(true);
    saveProgress({ done: true });
    if (submissionStatus !== "sent") {
      submitResults(questionResults, studentAnswers, attemptHistory, workingAnswers);
    }
  };

  const previousQuestion = () => {
    if (current === 0) return;
    const previous = current - 1;
    setCurrent(previous);
    setFeedback(null);
    setAttempts(attemptHistory[previous]?.length || 0);
    setStudentAnswer(studentAnswers[previous] || "");
    setWorking(workingAnswers[previous] || "");
    saveProgress({ current: previous });
  };

  const startReview = () => {
    setReviewMode(true);
    setCurrent(0);
    setFeedback(null);
    setStudentAnswer(studentAnswers[0] || "");
    setWorking(workingAnswers[0] || "");
  };

  const retrySubmission = () => submitResults(questionResults, studentAnswers, attemptHistory, workingAnswers);
  const score = questionResults.filter((result) => result === "correct").length;

  const feedbackMessage = () => {
    const percentage = (score / questions.length) * 100;
    if (percentage === 100) return "🌟 Perfect—you got everything right!";
    if (percentage >= 80) return "🎉 Great job—you did really well!";
    if (percentage >= 50) return "👍 Good effort. Review any red questions.";
    return "💪 Keep practising. Reviewing your answers will help.";
  };

  const renderProgress = () => (
    <div style={styles.progressWrapper}>
      {questionResults.map((result, index) => (
        <div key={questions[index].id} style={{
          ...styles.progressDot,
          backgroundColor: result === "correct" ? "#00b894" : result === "incorrect" ? "#d63031" : "#636e72",
          border: index === current && !done ? "3px solid #f9b710" : "2px solid transparent",
        }}>
          {index + 1}
        </div>
      ))}
    </div>
  );

  if (!nameConfirmed) {
    return (
  
        <div style={styles.container}>
          <div style={styles.nameCard}>
            <h1 style={styles.heading}>Standard Form Quiz</h1>
            <p>Please enter your name before you begin. Your results will be sent to Ryan when you finish.</p>
            <form onSubmit={confirmName}>
              <label htmlFor="student-name" style={styles.label}>Your name</label>
              <input id="student-name" autoFocus autoComplete="name" maxLength={80} value={studentName}
                onChange={(event) => setStudentName(event.target.value)} style={styles.nameInput} />
              <button type="submit" disabled={!studentName.trim()} style={styles.startBtn}>Start Quiz ➡️</button>
            </form>
          </div>
        </div>
   
    );
  }

  const q = questions[current];
  const checkedParts = parseParts(studentAnswers[current]);
  const partStatus = (index) => q.parts?.length > 1 && studentAnswers[current]
    ? matchesPart(checkedParts[index] || "", q.parts[index]) ? "correct" : "incorrect"
    : null;
  const partStyle = (index) => partStatus(index) === "correct" ? styles.correctField
    : partStatus(index) === "incorrect" ? styles.incorrectField : completedFieldStyle;
  const currentResult = questionResults[current];
  const completedFieldStyle =
    currentResult === "correct"
      ? styles.correctField
      : currentResult === "incorrect"
        ? styles.incorrectField
        : styles.unansweredField;

  return (
       <div style={styles.container}>
        <h1 style={styles.heading}>Standard Form</h1>
        <p style={styles.studentLabel}>Student: <strong>{studentName}</strong></p>
        {renderProgress()}

        {done && !reviewMode ? (
          <div style={styles.resultsBox}>
            <h2 style={{ fontFamily: "Inter, 'Segoe UI', sans-serif", color: "#17324d", fontWeight: 700 }}>Score: {score} / {questions.length}</h2>
            <p style={styles.feedbackText}>{feedbackMessage()}</p>
            {submissionStatus === "sending" && <p>Sending your results to Ryan…</p>}
            {submissionStatus === "sent" && (
              <div style={{ ...styles.statusLine, ...styles.sentMessage }}>
                <ResultIcon type="correct" size={25} />
                <span>Your results have been sent to Ryan.</span>
              </div>
            )}
            {submissionStatus === "failed" && (
              <div>
                <p style={styles.errorMessage}>Your quiz is saved, but the results could not be sent.</p>
                <button onClick={retrySubmission} style={styles.submitBtn}>Try Sending Again</button>
              </div>
            )}
            <button onClick={startReview} style={styles.reviewBtn}>🔍 Review Questions</button>
          </div>
        ) : (
          <div style={styles.card}>
            <h3 style={styles.questionHeading}>Question {current + 1} of {questions.length}</h3>
            {!q.parts?.length && <div style={styles.calculatorIndicator}>
              <CalculatorIcon allowed={q.calculator === "yes"} />
            </div>}
            <p style={styles.instruction}>
              {q.instruction}
              {q.parts?.length > 1 && <><br /><br /></>}
            </p>
            <style>{`
              .standard-form-conversion .katex { font-size: 1.5rem !important; color: #17324d; white-space: nowrap !important; }
              .standard-form-conversion .katex .text { font-size: 1em !important; }
              .standard-form-conversion .katex-display { margin: 0.35rem 0; }
              @media (max-width: 480px) { .standard-form-conversion .katex { font-size: 1.35rem !important; } }
            `}</style>
            {q.type !== "order" && q.parts.map((part, index) => <div key={index} style={{ marginBottom: 24 }}>
              {q.parts.length > 1 && <p style={styles.instruction}>({String.fromCharCode(97 + index)}) {part.instruction}</p>}
              <div style={styles.calculatorIndicator}><CalculatorIcon allowed={(part.calculator || q.calculator) === "yes"} /></div>
              <div className="wrap-math standard-form-conversion"
                style={{
                  background: "linear-gradient(120deg, #eff6ff, #f0fdfa)", border: "1px solid #c7ddef",
                  borderLeft: "4px solid #3985b5", borderRadius: 12, padding: "18px 24px",
                  margin: "16px 0 20px", overflowX: "auto", boxShadow: "0 3px 10px rgba(23,50,77,0.06)",
                }}><BlockMath math={part.latex} /></div>
              {part.instruction.startsWith("Calculate.") && <>
              <label htmlFor={`working-${q.id}-${index}`} style={styles.label}>Show your working:</label>
              <textarea id={`working-${q.id}-${index}`} rows={4} readOnly={reviewMode || canAdvance || partStatus(index) === "correct"}
                value={workingParts(reviewMode ? workingAnswers[current] : working)[index] || ""}
                onChange={(event) => setWorking(JSON.stringify({ ...workingParts(working), [index]: event.target.value }))}
                placeholder="Show any working here" style={{ ...styles.workingInput, ...partStyle(index) }} />
              </>}
              {q.parts.length === 1 && <label style={styles.label}>Answer:</label>}
              {part.standard ? <StandardFormAnswer
                partLabel={String.fromCharCode(97 + index)} readOnly={reviewMode || canAdvance || partStatus(index) === "correct"} fieldStyle={partStyle(index)}
                value={parseParts(reviewMode || canAdvance ? studentAnswers[current] : studentAnswer)[index] || ""}
                onChange={(value) => setStudentAnswer(JSON.stringify({ ...parseParts(studentAnswer), [index]: value }))} /> :
              <math-field aria-label={"Answer " + String.fromCharCode(97 + index)} readOnly={reviewMode || canAdvance || partStatus(index) === "correct"}
                style={{ ...styles.mathInput, ...partStyle(index) }}
                value={parseParts(reviewMode || canAdvance ? studentAnswers[current] : studentAnswer)[index] || ""}
                onInput={(event) => !reviewMode && !canAdvance && partStatus(index) !== "correct" && setStudentAnswer(JSON.stringify({ ...parseParts(studentAnswer), [index]: event.target.value }))} />}
              {partStatus(index) === "correct" && <p style={{ color: "#087f5b", fontWeight: 600 }} role="status">Correct!</p>}
              {!reviewMode && partStatus(index) === "incorrect" && <p style={{ color: "#c0392b", fontWeight: 600 }} role="status">
                {canAdvance ? "No attempts left." : `Try again. Attempts left: ${Math.max(0, 3 - (attemptHistory[current]?.length || 0))}`}
              </p>}
            </div>)}

            {q.type === "order" && <>
            <label style={styles.label}>Show your working:</label>
            <textarea rows={4} readOnly={reviewMode || canAdvance}
              value={reviewMode ? workingAnswers[current] : working}
              onChange={(event) => setWorking(event.target.value)}
              placeholder="Show any working here" style={{ ...styles.workingInput, ...completedFieldStyle }} />
            </>}

            {q.type === "order" && (
              <OrderingAnswer question={q} value={reviewMode || canAdvance ? studentAnswers[current] : studentAnswer}
                locked={reviewMode || canAdvance} onChange={setStudentAnswer} fieldStyle={currentResult ? completedFieldStyle : {}} />
            )}

            {reviewMode && (
              <div style={styles.reviewAnswer}>
                <div style={styles.statusLine}>
                  <ResultIcon type={questionResults[current] === "correct" ? "correct" : "incorrect"} />
                  <strong style={{ color: questionResults[current] === "correct" ? "#087f5b" : "#c0392b" }}>
                    {questionResults[current] === "correct" ? "Correct:" : "Correct answer:"}
                  </strong>
                </div>
                <BlockMath math={q.displayAnswer} />
              </div>
            )}
            {!reviewMode && feedback && <div style={styles.feedbackBox}>{feedback}</div>}
            {!reviewMode && !canAdvance && <button onClick={checkAnswer} style={styles.submitBtn}>Check Answer</button>}

            {(canAdvance || reviewMode) && (
              <div>
                {current > 0 && <button onClick={previousQuestion} style={styles.secondaryBtn}>← Back</button>}
                <button onClick={nextQuestion} style={styles.nextBtn}>
                  {current + 1 < questions.length ? "Next ➡️" : reviewMode ? "Finish Review" : "Finish & Send 🎉"}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    
  );
}

const styles = {
  container: { padding: "2rem", fontFamily: "'Fredoka', cursive", backgroundColor: "#eaf4fc", minHeight: "100vh" },
  heading: { fontFamily: "Inter, 'Segoe UI', sans-serif", fontSize: "clamp(1.6rem, 3vw, 2.25rem)", fontWeight: 700, lineHeight: 1.25, letterSpacing: "-0.035em", color: "#17324d", textAlign: "center", maxWidth: 760, margin: "0 auto 1.5rem", paddingBottom: "1rem", borderBottom: "3px solid #b8d4e8" },
  questionHeading: { fontFamily: "Inter, 'Segoe UI', sans-serif", fontSize: "1rem", fontWeight: 600, letterSpacing: "0.025em", color: "#475569", margin: "0 0 1.25rem" },
  studentLabel: { textAlign: "center", marginBottom: "1rem" },
  card: { backgroundColor: "#fff", padding: "2rem", borderRadius: 12, boxShadow: "0 4px 8px rgba(0,0,0,0.1)", maxWidth: 700, margin: "1rem auto 0" },
  nameCard: { backgroundColor: "#fff", padding: "2rem", borderRadius: 12, boxShadow: "0 4px 8px rgba(0,0,0,0.1)", maxWidth: 520, margin: "4rem auto" },
  nameInput: { display: "block", width: "100%", boxSizing: "border-box", padding: "0.75rem", margin: "0.5rem 0 1rem", border: "1px solid #aaa", borderRadius: 8, fontSize: "1.1rem" },
  startBtn: { padding: "0.7rem 1.3rem", backgroundColor: "#6c5ce7", color: "white", fontWeight: "bold", border: 0, borderRadius: 7, cursor: "pointer" },
  calculatorIndicator: { marginBottom: 12, display: "flex", justifyContent: "flex-end" },
  instruction: { fontWeight: 700, fontSize: "1.05rem" },
  label: { fontFamily: "Inter, 'Segoe UI', sans-serif", color: "#334155", fontWeight: 600, marginTop: 10, display: "block" },
  mathInput: { display: "block", width: "100%", boxSizing: "border-box", fontSize: "1.25rem", padding: "0.65rem", borderRadius: 8, border: "1px solid #ccc", marginTop: 8 },
  workingInput: { width: "100%", boxSizing: "border-box", borderRadius: 8, border: "1px solid #ccc", padding: "0.75rem", fontSize: "1rem", fontFamily: "'Fredoka', cursive", marginTop: 8, marginBottom: 20 },
  unansweredField: { backgroundColor: "#fff", border: "1px solid #ccc" },
  correctField: { backgroundColor: "#e8f8ee", border: "2px solid #42a96b" },
  incorrectField: { backgroundColor: "#fdeaea", border: "2px solid #d95858" },
  feedbackBox: { marginTop: "1rem" },
  statusLine: { display: "flex", alignItems: "center", gap: 10 },
  reviewAnswer: { marginTop: "1rem", padding: "1rem", backgroundColor: "#f5f6fa", borderRadius: 8 },
  submitBtn: { marginTop: "1rem", padding: "0.6rem 1.2rem", backgroundColor: "#0984e3", color: "white", fontWeight: "bold", border: 0, borderRadius: 6, cursor: "pointer", marginRight: "0.75rem" },
  nextBtn: { marginTop: "1rem", padding: "0.6rem 1.2rem", backgroundColor: "#00b894", color: "white", fontWeight: "bold", border: 0, borderRadius: 6, cursor: "pointer" },
  secondaryBtn: { marginTop: "1rem", padding: "0.6rem 1.2rem", backgroundColor: "#636e72", color: "white", fontWeight: "bold", border: 0, borderRadius: 6, cursor: "pointer", marginRight: "0.75rem" },
  progressWrapper: { display: "flex", justifyContent: "center", gap: 10, marginBottom: "1.5rem", flexWrap: "wrap" },
  progressDot: { width: 36, height: 36, borderRadius: "50%", display: "flex", justifyContent: "center", alignItems: "center", fontWeight: "bold", color: "white" },
  resultsBox: { textAlign: "center", backgroundColor: "white", maxWidth: 700, margin: "2rem auto", padding: "2rem", borderRadius: 12, boxShadow: "0 4px 8px rgba(0,0,0,0.1)" },
  feedbackText: { fontSize: "1.25rem", margin: "1rem 0" },
  sentMessage: { color: "#087f5b", fontWeight: 700, justifyContent: "center", margin: "1rem 0" },
  errorMessage: { color: "#c0392b", fontWeight: 700 },
  reviewBtn: { marginTop: "1rem", padding: "0.6rem 1.2rem", backgroundColor: "#636e72", color: "white", fontWeight: "bold", border: 0, borderRadius: 6, cursor: "pointer" },
};


function CalculatorIcon({ allowed }) {
  const label = allowed ? "Calculator allowed" : "No calculator";

  return (
    <svg role="img" aria-label={label} width={56} height={40} viewBox="0 0 56 40"
      style={{ display: "block" }}>
      <title>{label}</title>
      <rect x="5" y="3" width="27" height="34" rx="4" fill="#f1f5f9" stroke="#334155" strokeWidth="2" />
      <rect x="10" y="8" width="17" height="7" rx="1" fill="#cbd5e1" stroke="#334155" />
      <path d="M11 21h2m5 0h2m5 0h2M11 27h2m5 0h2m5 0h2M11 32h2m5 0h2m5 0h2"
        stroke="#334155" strokeWidth="3" strokeLinecap="round" />
      {allowed ? (
        <path d="M37 22l5 5 9-12" fill="none" stroke="#138a5b" strokeWidth="4"
          strokeLinecap="round" strokeLinejoin="round" />
      ) : (
        <path d="M5 5l27 30M32 5L5 35" fill="none" stroke="#d64545" strokeWidth="4"
          strokeLinecap="round" />
      )}
    </svg>
  );
}


// Generate short feedback tones locally, without loading audio files.
let quizAudioContext;
async function playQuizFeedback(correct) {
  try {
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextClass) return;
    if (!quizAudioContext || quizAudioContext.state === "closed") quizAudioContext = new AudioContextClass();
    if (quizAudioContext.state === "suspended") await quizAudioContext.resume();
    const context = quizAudioContext;
    const start = context.currentTime;
    const oscillator = context.createOscillator();
    const volume = context.createGain();
    oscillator.type = correct ? "sine" : "triangle";
    oscillator.frequency.setValueAtTime(correct ? 880 : 240, start);
    if (correct) oscillator.frequency.setValueAtTime(1174.66, start + 0.07);
    else {
      oscillator.frequency.exponentialRampToValueAtTime(170, start + 0.1);
      oscillator.frequency.setValueAtTime(210, start + 0.14);
      oscillator.frequency.exponentialRampToValueAtTime(130, start + 0.28);
    }
    const duration = correct ? 0.2 : 0.3;
    volume.gain.setValueAtTime(0.0001, start);
    volume.gain.exponentialRampToValueAtTime(correct ? 0.15 : 0.14, start + 0.008);
    if (!correct) {
      volume.gain.exponentialRampToValueAtTime(0.0001, start + 0.11);
      volume.gain.setValueAtTime(0.0001, start + 0.14);
      volume.gain.exponentialRampToValueAtTime(0.14, start + 0.15);
    }
    volume.gain.exponentialRampToValueAtTime(0.0001, start + duration);
    oscillator.connect(volume);
    volume.connect(context.destination);
    oscillator.onended = () => { oscillator.disconnect(); volume.disconnect(); };
    oscillator.start(start);
    oscillator.stop(start + duration + 0.01);
  } catch {
    // Audio availability should never interrupt answer checking.
  }
}
